import { useEffect, useCallback } from 'react';
import { X as XIcon, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function streamUrl(dateipfad) {
  if (!dateipfad) return '';
  return `${API}/api/prozesse/medien/stream/${encodeURIComponent(dateipfad)}`;
}

function downloadUrl(dateipfad) {
  if (!dateipfad) return '';
  return `${API}/api/prozesse/medien/download/${encodeURIComponent(dateipfad)}`;
}

function isVideo(mediumOrMime) {
  if (!mediumOrMime) return false;
  if (typeof mediumOrMime === 'string') return mediumOrMime.startsWith('video/');
  if (mediumOrMime.dateityp === 'video') return true;
  if (mediumOrMime.mime_type && mediumOrMime.mime_type.startsWith('video/')) return true;
  return false;
}

export default function MediaLightbox({ items, currentIndex, onClose, onNavigate, t }) {
  const item = items?.[currentIndex];

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
    if (e.key === 'ArrowRight' && currentIndex < (items?.length || 0) - 1) onNavigate(currentIndex + 1);
  }, [currentIndex, items, onClose, onNavigate]);

  useEffect(() => {
    if (!item) return;
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [item, handleKey]);

  if (!item) return null;

  const url = item.url || streamUrl(item.dateipfad);
  const dlUrl = downloadUrl(item.dateipfad);
  const video = isVideo(item);
  const hasMulti = (items?.length || 0) > 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label={t?.('prozesse.medien.lightbox_close') || 'Schließen'}
      >
        <XIcon className="w-6 h-6" strokeWidth={2} />
      </button>

      {dlUrl && (
        <a
          href={dlUrl}
          download
          onClick={(e) => e.stopPropagation()}
          className="absolute top-4 right-16 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <Download className="w-5 h-5" strokeWidth={2} />
        </a>
      )}

      {hasMulti && currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex - 1); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="w-7 h-7" strokeWidth={2} />
        </button>
      )}

      {hasMulti && currentIndex < items.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex + 1); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="w-7 h-7" strokeWidth={2} />
        </button>
      )}

      <div onClick={e => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3">
        {video ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="max-w-[90vw] max-h-[80vh] rounded-lg shadow-2xl bg-black"
          />
        ) : (
          <img src={url} alt={item.dateiname || ''} className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg shadow-2xl" />
        )}
        {item.dateiname && (
          <div className="text-white/80 text-sm flex items-center gap-3">
            <span className="font-medium">{item.dateiname}</span>
            {hasMulti && <span className="text-white/50">{currentIndex + 1} / {items.length}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
